/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: BlacklistFormView.tsx
 🗓️ Created: 2025-01-21
 🧩 Description:
    Form view for creating and editing Blacklist entries.
    Manages phone numbers, patterns, and regex for call blocking.

 🔧 Notes:
    - Supports number, pattern, and regex types
    - Scope selection (inbound, outbound, all)
    - Source tracking (manual, system, api)
    - Domain-scoped data

 🔁 Last Updated: 2025-11-08 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Ban, Search, Copy, Plus } from 'lucide-react';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { FormBox } from '../../components/ui/FormBox';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { navigateToBlacklistDuplicate } from './blacklistActions';
import {
  fetchBlacklistEntry,
  createBlacklistEntry,
  updateBlacklistEntry,
  deleteBlacklistEntry,
  fetchBlacklistEntries,
} from './api';
import { BlacklistEntry, BlacklistEntryFormState, BlacklistType, BlacklistScope, BlacklistSource } from './types';
import { BlacklistGeneralSection } from './components/BlacklistGeneralSection';

type BlacklistFormData = BlacklistEntryFormState;

const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

const DEFAULT_FORM_DATA: BlacklistFormData = {
  type: 'number',
  value: '',
  description: '',
  source: 'manual',
  scope: 'inbound',
  enabled: true,
  destinationModule: 'hangup',
  destinationValue: '',
};

export default function BlacklistFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();

  const [formData, setFormData] = useState<BlacklistFormData>(deepClone(DEFAULT_FORM_DATA));
  const [originalData, setOriginalData] = useState<BlacklistFormData>(deepClone(DEFAULT_FORM_DATA));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(isEditMode || Boolean(duplicateId));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; value: string; description?: string | null }>>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const lastSaveTimestamp = useRef(0);
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);
  const quickListLoadingRef = useRef(false);
  const navigateRef = useRef(navigate);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Update refs when values change (but don't trigger effects)
  useEffect(() => {
    navigateRef.current = navigate;
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
    tRef.current = t;
  }, [navigate, showError, showInfo, t]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      !duplicateId &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData(deepClone(DEFAULT_FORM_DATA));
      setOriginalData(deepClone(DEFAULT_FORM_DATA));
      setErrors({});

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, duplicateId, resetParam, searchParams]);

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('blacklist.editEntry')} - ${formData.value || ''}` : t('blacklist.createEntry')),
    [isEditMode, t, formData.value],
  );

  const typeOptions = useMemo(
    () => [
      { value: 'number', label: t('blacklist.types.number') },
      { value: 'pattern', label: t('blacklist.types.pattern') },
      { value: 'regex', label: t('blacklist.types.regex') },
    ],
    [t],
  );

  const scopeOptions = useMemo(
    () => [
      { value: 'inbound', label: t('blacklist.scopes.inbound') },
      { value: 'outbound', label: t('blacklist.scopes.outbound') },
      { value: 'all', label: t('blacklist.scopes.all') },
    ],
    [t],
  );

  const sourceOptions = useMemo(
    () => [
      { value: 'manual', label: t('blacklist.sources.manual') },
      { value: 'system', label: t('blacklist.sources.system') },
      { value: 'api', label: t('blacklist.sources.api') },
    ],
    [t],
  );

  const hasChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalData),
    [formData, originalData],
  );

  const handleFieldChange = <K extends keyof BlacklistFormData>(
    field: K,
    value: BlacklistFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const applyLoadedData = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (entry: any, { duplicate }: { duplicate: boolean }) => {
      const loaded: BlacklistFormData = {
        type: entry.type || 'number',
        value: duplicate ? '' : entry.value || '',
        description: entry.description || '',
        source: entry.source || 'manual',
        scope: entry.scope || 'inbound',
        enabled: entry.enabled ?? true,
        destinationModule: entry.destinationModule ?? entry.destination_module ?? 'hangup',
        destinationValue: entry.destinationValue ?? entry.destination_value ?? '',
      };

      setFormData(loaded);
      setErrors({});

      if (duplicate) {
        setOriginalData(deepClone(DEFAULT_FORM_DATA));
        showInfoRef.current(tRef.current('blacklist.messages.duplicateHint'));
      } else {
        setOriginalData(deepClone(loaded));
      }
    },
    [],
  );

  const loadBlacklistEntry = useCallback(
    async (entryId: string, { duplicate }: { duplicate: boolean }) => {
      // Prevent loading the same ID multiple times
      const loadKey = duplicate ? `duplicate-${entryId}` : entryId;
      if (lastLoadedIdRef.current === loadKey) {
        setIsLoading(false);
        isLoadingRef.current = false;
        return;
      }

      // Prevent concurrent loads
      if (isLoadingRef.current) {
        return;
      }

      setIsLoading(true);
      isLoadingRef.current = true;
      lastLoadedIdRef.current = loadKey;

      try {
        const result = await fetchBlacklistEntry(entryId);
        applyLoadedData(result, { duplicate });
      } catch (error) {
        lastLoadedIdRef.current = null;
        showErrorRef.current(tRef.current('blacklist.messages.loadError'));
        navigateRef.current('/pbx/incoming-tools/blacklist');
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [applyLoadedData],
  );

  useEffect(() => {
    if (isEditMode && id) {
      loadBlacklistEntry(id, { duplicate: false });
    } else if (duplicateId) {
      loadBlacklistEntry(duplicateId, { duplicate: true });
    }
  }, [duplicateId, id, isEditMode, loadBlacklistEntry]);

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!formData.value.trim()) {
      nextErrors.value = t('blacklist.validation.valueRequired');
    }

    if (formData.type === 'number' && formData.value.trim()) {
      const numberPattern = /^\+?[\d*#]+$/;
      if (!numberPattern.test(formData.value.trim())) {
        nextErrors.value = t('blacklist.validation.invalidNumber');
      }
    }

    if (formData.type === 'regex' && formData.value.trim()) {
      try {
        // eslint-disable-next-line no-new
        new RegExp(formData.value.trim());
      } catch {
        nextErrors.value = t('blacklist.validation.invalidRegex');
      }
    }

    setErrors(nextErrors);
    return { isValid: Object.keys(nextErrors).length === 0, errors: nextErrors };
  }, [formData, t]);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent): Promise<BlacklistEntry | undefined> => {
      event?.preventDefault();

      const validation = validateForm();
      if (!validation.isValid) {
        const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
        showError(`${t('common.validationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
        return undefined;
      }

      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) {
        return undefined;
      }
      lastSaveTimestamp.current = now;

      setIsSaving(true);
      try {
        const payload = {
          type: formData.type,
          value: formData.value.trim(),
          description: formData.description.trim() || null,
          source: formData.source,
          scope: formData.scope,
          enabled: formData.enabled,
          destinationModule: formData.destinationModule.trim() || null,
          destinationValue: formData.destinationValue.trim() || null,
        };

        const result = isEditMode
          ? await updateBlacklistEntry(id!, payload)
          : await createBlacklistEntry(payload);

        showSuccess(
          isEditMode
            ? t('blacklist.messages.updateSuccess')
            : t('blacklist.messages.createSuccess'),
        );

        const sanitizedData: BlacklistFormData = {
          type: formData.type,
          value: formData.value.trim(),
          description: formData.description.trim(),
          source: formData.source,
          scope: formData.scope,
          enabled: formData.enabled,
          destinationModule: formData.destinationModule.trim(),
          destinationValue: formData.destinationValue.trim(),
        };
        setOriginalData(deepClone(sanitizedData));

        // Return saved data for useSaveWithAction
        return result;
      } catch (error) {
        showError(error?.message || t('blacklist.messages.createError'));
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, hasChanges, id, isEditMode, showError, showInfo, showSuccess, t, validateForm],
  );

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as BlacklistEntry;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/blacklist',
    newPath: '/pbx/incoming-tools/blacklist/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Blacklist entries use ID for navigation
      return `/pbx/incoming-tools/blacklist/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'blacklist:saveAndNewData'
  });

  const handleReset = () => {
    setFormData(deepClone(originalData));
    setErrors({});
  };

  const handleCancel = () => {
    if (isEditMode) {
      handleReset();
    } else {
      navigate('/pbx/incoming-tools/blacklist');
    }
  };

  const handleDelete = () => setShowDeleteModal(true);

  // Duplicate current blacklist entry from the fixed header action
  const handleDuplicateFromForm = () => {
    if (!isEditMode || !id) return;
    navigateToBlacklistDuplicate(navigate, id);
  };

  // Load quick list items
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'value',
        sortOrder: 'asc',
      });

      const result = await fetchBlacklistEntries({
        page: 1,
        limit: 100,
        sortBy: 'value',
        sortOrder: 'asc',
      });
      const items = Array.isArray(result?.data)
        ? (result.data as Array<{ id: number; value: string; description?: string | null }>)
        : [];
      setQuickListItems(items);
    } catch {
      showError(t('blacklist.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [showError, t]);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.value.toLowerCase().includes(query) ||
      (item.description || '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  const confirmDelete = useCallback(async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      await deleteBlacklistEntry(id);

      showSuccess(t('blacklist.messages.deleteSuccess'));
      setShowDeleteModal(false);
      navigate('/pbx/incoming-tools/blacklist');
    } catch (error) {
      showError(error?.message || t('blacklist.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [id, navigate, showError, showSuccess, t]);

  // Reset load guard on unmount - ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<Ban className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('blacklist.actions.list', 'List')}
        onBack={() => navigate('/pbx/incoming-tools/blacklist')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('blacklist.quickList.tooltip', 'Quick Search'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('blacklist.actions.add'),
            onClick: () => navigate('/pbx/incoming-tools/blacklist/new'),
          },
        ]}
      />

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <BlacklistGeneralSection
          formData={formData}
          errors={errors}
          typeOptions={typeOptions}
          scopeOptions={scopeOptions}
          sourceOptions={sourceOptions}
          onFieldChange={handleFieldChange}
          t={t}
        />

        <FormBox
          title={t('blacklist.fields.destination.label')}
          icon={<Ban className="h-5 w-5" />}
          description={t('blacklist.fields.destination.tooltip')}
        >
          <ModuleDestinationSelect
            moduleValue={formData.destinationModule}
            destinationValue={formData.destinationValue}
            onModuleChange={(value) =>
              handleFieldChange('destinationModule', value || '')}
            onDestinationChange={(value) =>
              handleFieldChange('destinationValue', value || '')}
            modulePlaceholder={t('blacklist.fields.destination.modulePlaceholder')}
            destinationPlaceholder={t(
              'blacklist.fields.destination.destinationPlaceholder',
            )}
          />
        </FormBox>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <h4 className="mb-2 text-sm font-semibold text-blue-900 dark:text-blue-100">
            {t('blacklist.help.title')}
          </h4>
          <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
            <li>
              • <strong>{t('blacklist.types.number')}:</strong>{' '}
              {t('blacklist.help.number')}
            </li>
            <li>
              • <strong>{t('blacklist.types.pattern')}:</strong>{' '}
              {t('blacklist.help.pattern')}
            </li>
            <li>
              • <strong>{t('blacklist.types.regex')}:</strong>{' '}
              {t('blacklist.help.regex')}
            </li>
          </ul>
        </div>
      </form>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? handleDelete : undefined}
        onReset={hasChanges ? handleReset : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        isLoading={isLoading}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="blacklist:saveActionPreference"
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('blacklist.messages.deleteTitle') || 'Delete Blacklist Entry'}
        message={t('blacklist.messages.confirmDelete', {
          value: formData.value || '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('blacklist.quickList.title', 'Blacklist')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/incoming-tools/blacklist/${item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/incoming-tools/blacklist/${item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.value}
            </div>
            {item.description && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('blacklist.quickList.searchPlaceholder', 'Search by value or description')}
        emptyMessage={t('blacklist.quickList.empty', 'No blacklist entries found')}
        headerIcon={<Ban className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

